import { archerClient, getSseHeaders, orchestrationUrl } from '../services/archerClient';
import type { SseEvent } from '../interfaces/chat';

/**
 * Archer Orchestration API — session lifecycle + chat. Ported from the
 * archer-ai example client; JSON calls go through the shared axios instance,
 * the chat stream uses fetch so we can read the SSE byte stream incrementally.
 */

interface CreateSessionResponse {
  sessionId: string;
}

export async function createSession(
  chatbotId: string,
  chatbotVersionId?: string,
): Promise<string> {
  const body: Record<string, unknown> = {
    chatbotId,
    clientContext: {
      channel: { type: 'web-portal' },
      localization: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        localeCode: navigator.language || 'en-US',
      },
    },
  };
  if (chatbotVersionId) body.chatbotVersionId = chatbotVersionId;

  const { data } = await archerClient.post<CreateSessionResponse>(
    orchestrationUrl('/sessions'),
    body,
  );
  return data.sessionId;
}

interface ChatMessageAccepted {
  messageId: string;
}

export async function sendMessage(
  sessionId: string,
  text: string,
  callbackId?: string | null,
): Promise<string> {
  const body: Record<string, unknown> = { content: { type: 'TEXT', text } };
  if (callbackId) body.callbackId = callbackId;

  const { data } = await archerClient.post<ChatMessageAccepted>(
    orchestrationUrl(`/sessions/${sessionId}/messages`),
    body,
    {
      // 202 == accepted/queued; the actual reply arrives over the SSE stream.
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    },
  );
  // This is the persisted *user* message's id (used as a pagination cursor
  // for recovery below), not the assistant reply's id — the backend assigns
  // the reply its own id once the turn finishes.
  return data.messageId;
}

export async function getMessage(
  sessionId: string,
  messageId: string,
): Promise<Record<string, unknown>> {
  const { data } = await archerClient.get<Record<string, unknown>>(
    orchestrationUrl(`/sessions/${sessionId}/messages/${messageId}`),
  );
  return data;
}

export interface OrchestrationMessage {
  messageId: string;
  role: string;
  content: { type: string; text?: string } | Record<string, unknown>;
  createdAt: string;
}

/**
 * Lists messages persisted after `afterMessageId` (exclusive), for recovering
 * a reply that finished processing while the SSE connection was down — the
 * response lands in durable storage regardless of which pod handled the
 * turn, but Redis pub/sub only fans it out to the pod that owns the live SSE
 * connection at the moment it's produced, so a dropped connection means the
 * live delivery is missed and must be recovered via this REST read instead.
 */
export async function listMessagesAfter(
  sessionId: string,
  afterMessageId: string,
  limit = 10,
): Promise<OrchestrationMessage[]> {
  const { data } = await archerClient.get<{ results: OrchestrationMessage[] }>(
    orchestrationUrl(`/sessions/${sessionId}/messages`),
    { params: { after: afterMessageId, limit } },
  );
  return data.results ?? [];
}

export async function closeSession(sessionId: string): Promise<void> {
  await archerClient.delete(orchestrationUrl(`/sessions/${sessionId}`), {
    validateStatus: () => true, // best-effort cleanup
  });
}

/**
 * Opens the long-lived SSE chat stream. Resolves `connected` once the server
 * has accepted the connection (HTTP 200) and registered the emitter — callers
 * must await it before POSTing a message, else the engine rejects with
 * "no active SSE connection". A per-turn `[DONE]` sentinel is surfaced as a
 * `done` event WITHOUT tearing down the connection; only a real TCP close
 * (reader done) fires `onDone`.
 */
export function openSseStream(
  sessionId: string,
  onEvent: (event: SseEvent) => void,
  onError: (error: string) => void,
  onDone: () => void,
  onActivity?: () => void,
): { controller: AbortController; connected: Promise<void> } {
  const controller = new AbortController();
  const url = orchestrationUrl(`/sessions/${sessionId}/chat`);

  let resolveConnected!: () => void;
  let rejectConnected!: (err: Error) => void;
  const connected = new Promise<void>((resolve, reject) => {
    resolveConnected = resolve;
    rejectConnected = reject;
  });

  fetch(url, { method: 'GET', headers: getSseHeaders(), signal: controller.signal })
    .then((response) => {
      if (!response.ok) {
        onError(`SSE connection failed: ${response.status}`);
        rejectConnected(new Error(`SSE connection failed: ${response.status}`));
        return;
      }
      const reader = response.body?.getReader();
      if (!reader) {
        onError('No response body');
        rejectConnected(new Error('No response body'));
        return;
      }

      resolveConnected();

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType: string | null = null;

      const read = (): void => {
        reader
          .read()
          .then(({ done, value }) => {
            if (done) {
              onDone();
              return;
            }

            // Any bytes at all (including SSE comment/ping lines) prove the
            // pod on the other end of this connection is still alive.
            onActivity?.();

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('event:')) {
                currentEventType = line.slice(6).trim();
              } else if (line.startsWith('data:')) {
                const data = line.slice(5).trim();
                if (data === '[DONE]') {
                  onEvent({
                    id: crypto.randomUUID(),
                    type: 'done',
                    data: '[DONE]',
                    timestamp: new Date(),
                  });
                  currentEventType = null;
                  continue;
                }
                onEvent({
                  id: crypto.randomUUID(),
                  type: currentEventType,
                  data,
                  timestamp: new Date(),
                });
                currentEventType = null;
              }
            }

            read();
          })
          .catch((err: Error) => {
            if (err.name !== 'AbortError') onError(err.message);
          });
      };

      read();
    })
    .catch((err: Error) => {
      if (err.name !== 'AbortError') {
        onError(err.message);
        rejectConnected(err);
      }
    });

  return { controller, connected };
}
